import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  House, CarProfile, ClockCounterClockwise, ChartBar, Coins,
  ListChecks, ShieldCheck, VideoCamera, SignOut, Users as UsersIcon,
  Sparkle, Buildings, Gear, Notebook, CashRegister, WarningOctagon, Shield,
  DownloadSimple, Receipt, LockKey, TicketIcon, Bank,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import GlobalVehicleActivityMonitor from "@/components/GlobalVehicleActivityMonitor";

// Rol bazlı görünürlük — kısıtlı sayfalar sadece admin/super_admin görür
const ADMIN_ONLY = new Set([
  "/admin/users", "/admin/camera-test", "/admin/camera-raw", "/admin/audit", "/admin/backup",
  "/admin/kvkk", "/admin/security", "/admin/settings", "/admin/bank-transfers",
]);

const NAV = [
  { to: "/admin/dashboard", label: "Panel", icon: House },
  { to: "/admin/kasa", label: "Kasa", icon: CashRegister },
  { to: "/admin/bank-transfers", label: "Banka Aktarımları ve Giderler", icon: Bank },
  { to: "/admin/payments-ledger", label: "Tahsilatlar", icon: Receipt },
  { to: "/admin/live", label: "Canlı Otopark", icon: CarProfile },
  { to: "/admin/reports", label: "Raporlar & Geçmiş", icon: ChartBar },
  { to: "/admin/tariffs", label: "Tarifeler", icon: Coins },
  { to: "/admin/unpaid", label: "Ödenmemiş", icon: ListChecks },
  { to: "/admin/unmatched", label: "Giriş Yok / Manuel", icon: WarningOctagon },
  { to: "/admin/access", label: "Beyaz/Kara Liste", icon: ShieldCheck },
  { to: "/admin/plans", label: "Abonelik Planları", icon: Sparkle },
  { to: "/admin/plate-subs", label: "Plaka Abonelikleri", icon: Sparkle },
  { to: "/admin/plate-verifications", label: "Plaka Onayları", icon: LockKey },
  { to: "/admin/subscription-payments", label: "Abonelik Ödemeleri", icon: Receipt },
  { to: "/admin/coupons", label: "Kupon Kodları", icon: TicketIcon },
  { to: "/admin/corporate", label: "Kurumsal Hesaplar", icon: Buildings },
  { to: "/admin/users", label: "Kullanıcılar", icon: UsersIcon },
  { to: "/admin/camera-test", label: "Kamera Test", icon: VideoCamera },
  { to: "/admin/camera-raw", label: "Kamera Ham Veri", icon: VideoCamera },
  { to: "/admin/audit", label: "Denetim Kaydı", icon: Notebook },
  { to: "/admin/backup", label: "Yedekleme", icon: DownloadSimple },
  { to: "/admin/kvkk", label: "KVKK", icon: Shield },
  { to: "/admin/security", label: "Güvenlik", icon: LockKey },
  { to: "/admin/settings", label: "Ayarlar", icon: Gear },
];

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const role = user?.role;
  const isFullAdmin = role === "admin" || role === "super_admin";
  const visibleNav = NAV.filter((item) => isFullAdmin || !ADMIN_ONLY.has(item.to));

  const onLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className="hidden md:flex md:flex-col w-64 border-r border-border bg-card">
        <div className="px-6 py-6 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-primary flex items-center justify-center rounded-sm">
              <CarProfile size={22} weight="fill" className="text-primary-foreground" />
            </div>
            <div>
              <div className="font-bold text-lg leading-tight">OTOPARK</div>
              <div className="text-xs text-muted-foreground uppercase tracking-widest">Yönetim</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {visibleNav.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${to.replace(/\//g, "-").slice(1)}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground hover:bg-muted"
                }`
              }
            >
              <Icon size={18} weight="regular" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-border">
          <div className="text-xs text-muted-foreground mb-2">Oturum</div>
          <div className="text-sm font-medium truncate" data-testid="admin-user-name">{user?.name}</div>
          <div className="text-xs text-muted-foreground uppercase tracking-wide">{user?.role}</div>
          <Button
            data-testid="admin-logout-btn"
            variant="outline"
            size="sm"
            className="w-full mt-3 rounded-sm"
            onClick={onLogout}
          >
            <SignOut size={16} /> Çıkış
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 bg-card border-b border-border px-4 py-3 flex items-center justify-between">
        <div className="font-bold">OTOPARK</div>
        <Button variant="ghost" size="sm" onClick={onLogout} data-testid="admin-logout-mobile">
          <SignOut size={16} />
        </Button>
      </div>

      <main className="flex-1 min-w-0 pt-16 md:pt-0 pb-24 md:pb-0">
        <div className="p-4 md:p-8 max-w-[1400px] mx-auto">
          {children}
        </div>

        {/* Mobile bottom nav */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-card border-t border-border overflow-x-auto">
          <div className="flex">
            {visibleNav.slice(0, 5).map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `flex-1 min-w-fit flex flex-col items-center gap-1 px-3 py-2 text-[10px] font-medium ${
                    isActive ? "text-primary" : "text-muted-foreground"
                  }`
                }
              >
                <Icon size={20} />
                <span className="whitespace-nowrap">{label}</span>
              </NavLink>
            ))}
          </div>
        </div>
      </main>

      {/* Global vehicle entry/exit activity popup */}
      <GlobalVehicleActivityMonitor />
    </div>
  );
}
